import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  TextField,
  MenuItem,
  Chip,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { Screen } from 'src/components/screen';
import { contentBox, formCard, listCard } from './styles';

const AutomationsScreen = () => {
  const [automations, setAutomations] = useState<
    { id: string; title: string; status: string; desc: string }[]
  >([
    {
      id: 'review-rescue',
      title: 'Review rescue',
      desc: 'Auto-draft replies when rating < 4.2 and queue for approval.',
      status: 'Active',
    },
    {
      id: 'gap-hour',
      title: 'Gap-hour booster',
      desc: 'Trigger social+SMS when evening sales dip 10% vs last week.',
      status: 'Active',
    },
    {
      id: 'inventory-watch',
      title: 'Inventory watch',
      desc: 'Alert when SKUs trend down and recommend bundles.',
      status: 'Paused',
    },
  ]);
  const [newName, setNewName] = useState('');
  const [newTrigger, setNewTrigger] = useState('threshold');
  const [newAction, setNewAction] = useState('notify');
  const [tips, setTips] = useState<string[]>([]);
  const quickTemplates = [
    {
      title: 'Low review alert',
      trigger: 'review',
      action: 'draft',
      name: 'Alert me on 1★ reviews',
    },
    {
      title: 'Evening slowdown',
      trigger: 'schedule',
      action: 'campaign',
      name: 'Boost 6-9pm sales',
    },
    {
      title: 'Inventory watch',
      trigger: 'inventory',
      action: 'notify',
      name: 'Low stock heads-up',
    },
  ];

  const helperTips = useMemo(
    () => [
      'Example trigger: “When daily sales drop 10% vs last week”',
      'Example trigger: “When a new 1★ review arrives”',
      'Example action: “Send me a Slack alert + draft a reply”',
      'Example action: “Schedule an Instagram post for tonight”',
      'Example action: “Update a shared dashboard widget”',
    ],
    [],
  );

  const handleDelete = (id: string) => {
    setAutomations(prev => prev.filter(a => a.id !== id));
  };

  const handleAdd = () => {
    if (!newName.trim()) return;
    setAutomations(prev => [
      ...prev,
      {
        id: `custom-${Date.now()}`,
        title: newName.trim(),
        desc: `${newTrigger} → ${newAction}`,
        status: 'Draft',
      },
    ]);
    setTips(helperTips.sort(() => 0.5 - Math.random()).slice(0, 2));
    setNewName('');
    setNewTrigger('threshold');
    setNewAction('notify');
  };

  const triggerOptions = [
    { value: 'threshold', label: 'When a metric crosses a threshold' },
    { value: 'schedule', label: 'On a schedule (daily/weekly)' },
    { value: 'review', label: 'When a new review appears' },
    { value: 'inventory', label: 'When stock drops or trends down' },
  ];

  const actionOptions = [
    { value: 'notify', label: 'Send me an alert' },
    { value: 'draft', label: 'Draft a reply or post' },
    { value: 'campaign', label: 'Launch a campaign template' },
    { value: 'update', label: 'Update a dashboard/widget' },
  ];

  return (
    <Screen>
      <Box sx={contentBox}>
        <Paper elevation={0} sx={formCard}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <BoltIcon color="primary" />
              <Typography variant="h6">Create an automation</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Pick a trigger and an action. Use plain language; we’ll wire it up
              for you.
            </Typography>
            <TextField
              fullWidth
              label="Name"
              placeholder="e.g., Evening slow-down alert"
              value={newName}
              onChange={e => setNewName(e.target.value)}
            />
            <FormControl fullWidth>
              <InputLabel id="trigger-label">Trigger</InputLabel>
              <Select
                labelId="trigger-label"
                value={newTrigger}
                label="Trigger"
                onChange={(e: SelectChangeEvent) =>
                  setNewTrigger(e.target.value)
                }>
                {triggerOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="action-label">Action</InputLabel>
              <Select
                labelId="action-label"
                value={newAction}
                label="Action"
                onChange={(e: SelectChangeEvent) =>
                  setNewAction(e.target.value)
                }>
                {actionOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAdd}>
                Add automation
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() =>
                  setTips(
                    helperTips.sort(() => 0.5 - Math.random()).slice(0, 2),
                  )
                }>
                Show examples
              </Button>
            </Stack>
            <Stack spacing={1}>
              <Typography variant="subtitle2" color="text.secondary">
                Or start with a template
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
                {quickTemplates.map(t => (
                  <Chip
                    key={t.title}
                    label={t.title}
                    onClick={() => {
                      setNewName(t.name);
                      setNewTrigger(t.trigger);
                      setNewAction(t.action);
                    }}
                  />
                ))}
              </Stack>
            </Stack>
            {tips.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
                {tips.map(t => (
                  <Chip key={t} label={t} />
                ))}
              </Stack>
            )}
          </Stack>
        </Paper>

        <Paper elevation={0} sx={listCard}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            mb={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <BoltIcon color="primary" />
              <Typography variant="h6">Automations in flight</Typography>
            </Stack>
          </Stack>
          {automations.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No automations yet. Create one to get started.
            </Typography>
          ) : (
            <List dense disablePadding>
              {automations.map(auto => (
                <React.Fragment key={auto.id}>
                  <ListItem
                    disableGutters
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleDelete(auto.id)}
                        aria-label="Delete automation">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    }>
                    <ListItemText
                      primaryTypographyProps={{ variant: 'subtitle2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                      primary={`${auto.title} · ${auto.status}`}
                      secondary={auto.desc}
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      </Box>
    </Screen>
  );
};

export { AutomationsScreen };
